import { useState, useEffect, useCallback, Component } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ImageCropDialog from '../../components/ImageCropDialog';
import {
  X, Save, Printer, Camera, Plus, Trash2, Loader2, Sparkles, Package,
  Layers, ChevronDown, FileDown, AlertTriangle, History, ClipboardCheck,
  ExternalLink,
} from 'lucide-react';

// Safety-Net: wenn IRGENDETWAS beim Rendern des Begleitdokuments
// crasht (z. B. ein Document hat einen verklemmten Wert oder das
// Template eine Sektion ohne `options`), zeigen wir statt eines
// weißen Bildschirms eine Fehlermeldung mit Stack-Hinweis an —
// damit der User auf Production die Ursache melden kann.
class EditorErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[OrderDocumentEditor] Render-Crash:', error, info);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
           onClick={this.props.onClose} data-testid="doc-render-error">
        <div className="bg-white rounded-xl p-6 max-w-lg" onClick={e => e.stopPropagation()}>
          <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" /> Begleitdokument konnte nicht angezeigt werden
          </h3>
          <p className="text-xs text-gray-600 mb-3">
            Beim Aufbau der Eingabemaske kam es zu einem unerwarteten Fehler.
            Bitte schicke uns folgenden Text — damit können wir die Ursache nachvollziehen:
          </p>
          <pre className="text-[10px] bg-gray-50 border border-gray-200 rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap break-all">
{String(this.state.error?.message || this.state.error)}
{this.state.error?.stack ? '\n' + this.state.error.stack.split('\n').slice(0, 5).join('\n') : ''}
          </pre>
          <Button onClick={this.props.onClose} variant="outline" className="text-sm mt-3">Schließen</Button>
        </div>
      </div>
    );
  }
}

function OrderDocumentEditorInner({ orderId, docId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeDocId, setActiveDocId] = useState(null);
  const [msg, setMsg] = useState('');
  const [ocrFor, setOcrFor] = useState(null);
  // Etappe C (27.02.2026): Crop-Dialog vor jedem OCR-Aufruf.
  const [cropReq, setCropReq] = useState(null);  // { file, hint, cb }
  const [inventory, setInventory] = useState([]);
  const [systems, setSystems] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // Two modes: multi-doc via orderId (Kanban), or single-doc via docId (Archive)
    const docUrl = docId
      ? `/api/order-documents/${docId}`
      : `/api/order-documents/by-order/${orderId}`;
    const [r, invR, sysR] = await Promise.all([
      authFetch(docUrl),
      authFetch('/api/inventory'),
      authFetch('/api/systems'),
    ]);
    if (r.ok) {
      const d = await r.json();
      // Normalize single-doc response into multi-doc shape
      const normalized = docId
        ? { documents: d.document ? [d.document] : [], template: d.template, order: d.order || {} }
        : d;
      setData(normalized);
      if (normalized.documents?.length && !activeDocId) {
        setActiveDocId(normalized.documents[0].id);
      }
    } else {
      // Detaillierte Fehleranzeige — der User soll auf Production sofort
      // sehen, ob es ein Berechtigungs- (403) oder Daten-Problem (404/500) ist.
      let detail = '';
      try { detail = (await r.json()).detail || ''; } catch { /* ignore */ }
      setLoadError({ status: r.status, detail });
      setData({ documents: [], template: null, order: {} });
    }
    if (invR.ok) { const dd = await invR.json(); setInventory(dd.items || []); }
    if (sysR.ok) { const dd = await sysR.json(); setSystems(dd.systems || []); }
    setLoading(false);
  }, [orderId, docId, activeDocId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId, docId]);

  if (loading) return <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>;
  if (!data?.documents?.length) {
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-xl p-6 max-w-md" onClick={e => e.stopPropagation()} data-testid="doc-empty-state">
          <p className="text-sm text-[#32373c] font-semibold mb-2">
            {loadError ? `Dokument konnte nicht geladen werden (HTTP ${loadError.status})` : 'Dokument nicht gefunden'}
          </p>
          <p className="text-xs text-gray-500 mb-4">
            {loadError?.detail
              ? `Backend-Meldung: ${loadError.detail}`
              : (loadError?.status === 403
                  ? 'Deinem Benutzer fehlt die Berechtigung „Begleitdokumente ansehen" (order_documents.read). Ein Admin kann sie in den Einstellungen → Rechte freischalten.'
                  : 'Das zugehörige Dokument ist nicht mehr verfügbar.')}
          </p>
          <Button onClick={onClose} variant="outline" className="text-sm">Schließen</Button>
        </div>
      </div>
    );
  }

  const { documents, template, order } = data;
  const activeDoc = documents.find(d => d.id === activeDocId) || documents[0];
  if (!activeDoc) return null;
  const values = activeDoc.values || { header: {}, sections: {} };
  // Wenn Lager schon gebucht ist, dürfen nur noch Seriennummern angepasst
  // werden (z. B. bei Garantietausch). Komponenten-Felder werden gesperrt.
  const locked = !!order?.bomDeducted;

  const patchActive = (patch) => {
    setData(p => ({
      ...p, documents: p.documents.map(d => d.id === activeDoc.id ? { ...d, ...patch } : d),
    }));
  };
  const setHeader = (key, v) => patchActive({ values: { ...values, header: { ...values.header, [key]: v } } });
  const setSection = (sid, v) => patchActive({ values: { ...values, sections: { ...values.sections, [sid]: v } } });
  const setKommission = (v) => patchActive({ kommission: v });

  const save = async () => {
    setSaving(true); setMsg('');
    const r = await authFetch(`/api/order-documents/${activeDoc.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: activeDoc.values, kommission: activeDoc.kommission }),
    });
    if (r.ok) {
      setMsg('✓ Gespeichert'); setTimeout(() => setMsg(''), 2000);
      // Auto-Snapshot: revisionssichere PDF-Version persistieren (fire-and-forget).
      authFetch(`/api/order-documents/${activeDoc.id}/snapshot`, { method: 'POST' })
        .catch(() => { /* Snapshot ist best-effort, blockiert Save nicht */ });
    }
    else {
      const d = await r.json().catch(() => ({}));
      setMsg(d.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  const downloadPdf = async () => {
    if (!activeDoc?.id) return;
    // Blob-Fetch über authFetch, damit Auth-Header mitgesendet wird
    const r = await authFetch(`/api/order-documents/${activeDoc.id}/pdf?download=true`);
    if (!r.ok) { alert('PDF konnte nicht erzeugt werden'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const num = order.easybillDocumentNumber || 'dokument';
    const sys = (activeDoc.systemName || 'system').replace(/[^\w-]/g, '_');
    a.href = url; a.download = `Begleitdokument_${num}_${sys}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // 04.02.2026 (User-Umzug): Systemcheckliste gehört zum Auftrag, nicht in
  // den öffentlichen Dokumente-Pool. Der auftrags-gebundene Endpoint liefert
  // das gespeicherte PDF aus `db.technik_checklists.pdfBytes`.
  // 18.06.26 (User-Wunsch): „Aufmachen" = PDF in neuem Browser-Tab anzeigen
  // (dort ist Drucken/Herunterladen möglich), zusätzlich separater Download.
  const _fetchSystemChecklistBlob = async () => {
    const url = data?.systemChecklist?.downloadUrl;
    if (!url) return null;
    const r = await authFetch(url);
    if (!r.ok) { alert('Systemcheckliste konnte nicht geladen werden'); return null; }
    return await r.blob();
  };
  const openSystemChecklist = async () => {
    const blob = await _fetchSystemChecklistBlob();
    if (!blob) return;
    const objUrl = URL.createObjectURL(blob);
    window.open(objUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  };
  const downloadSystemChecklist = async () => {
    const blob = await _fetchSystemChecklistBlob();
    if (!blob) return;
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = data.systemChecklist.fileName
      || `Systemcheckliste_${order.easybillDocumentNumber || 'auftrag'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
  };

  const printDoc = async () => {
    await save();
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(renderPrintableHtml(template, activeDoc, order));
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  const addSystemDoc = async (systemId) => {
    const fd = new FormData();
    if (systemId) fd.append('system_id', systemId);
    const r = await authFetch(`/api/order-documents/by-order/${orderId}/add`, {
      method: 'POST', body: fd,
    });
    if (r.ok) { const doc = await r.json(); setActiveDocId(doc.id); await load(); }
  };

  const deleteActiveDoc = async () => {
    if (!window.confirm(`Dieses Dokument (${activeDoc.systemName || 'ohne System'}) wirklich löschen?`)) return;
    const r = await authFetch(`/api/order-documents/${activeDoc.id}`, { method: 'DELETE' });
    if (r.ok) { setActiveDocId(null); await load(); }
  };

  const regenerateDocs = async () => {
    if (!orderId) return;
    const ok = window.confirm(
      'Alle bestehenden Begleitdokumente dieses Auftrags werden gelöscht und auf Basis ' +
      'der aktuellen Auftragspositionen + Systemdatenbank neu erkannt.\n\n' +
      'Bereits eingetragene Seriennummern blockieren diesen Vorgang — der Server lehnt ihn ' +
      'in dem Fall ab. Fortfahren?'
    );
    if (!ok) return;
    setSaving(true); setMsg('');
    const r = await authFetch(`/api/order-documents/by-order/${orderId}/regenerate`, {
      method: 'POST',
    });
    if (r.ok) {
      const d = await r.json();
      setMsg(`✓ ${d.deleted} alte gelöscht, ${d.created} neu erkannt`);
      setActiveDocId(null);
      await load();
      setTimeout(() => setMsg(''), 3500);
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg(d.detail || 'Regenerieren fehlgeschlagen');
    }
    setSaving(false);
  };

  // Direkt-OCR (ohne Crop) — wird vom Crop-Dialog aus aufgerufen
  const performOcr = async (file, hint, onResult) => {
    const fd = new FormData();
    fd.append('file', file);
    if (hint) fd.append('hint', hint);
    const r = await authFetch('/api/order-documents/extract-serial', { method: 'POST', body: fd });
    if (r.ok) onResult(await r.json());
    else alert('OCR fehlgeschlagen');
  };

  // Etappe C: OCR mit vorgeschaltetem Crop-Dialog. Nutzer kann Bereich mit
  // der Seriennummer präzise zuschneiden, dann läuft die Extraktion NUR auf
  // dem Ausschnitt — deutlich zuverlässiger als auf dem Vollbild.
  const runOCR = async (file, hint, onResult) => {
    setCropReq({ file, hint, cb: onResult });
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        // 04.02.2026: Nur schließen wenn direkt auf das Overlay geklickt
        // wurde — Klicks aus geschachtelten Portalen wie dem
        // ImageCropDialog bubbeln sonst hierher und schlossen den
        // Dokument-Editor mit, obwohl der User nur „OCR starten" tippte.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] shadow-xl flex flex-col" onClick={e => e.stopPropagation()} data-testid="order-doc-editor">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0 gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-[#32373c]">{template.title || 'Produktbegleitdokument'}</h2>
            {activeDoc?.systemName && (
              <div className="text-lg font-bold text-[#9ec043] tracking-wide truncate mt-0.5" data-testid="doc-system-name">
                {activeDoc.systemName}
                {activeDoc.quantity > 1 && <span className="text-sm text-gray-500 font-medium ml-2">× {activeDoc.quantity}</span>}
              </div>
            )}
            <p className="text-[11px] text-gray-400 truncate">
              Auftrag {order.easybillDocumentNumber || order.id?.slice(0, 8)} · {order.customerCompany || order.customerName || '—'}
              {documents.length > 1 && <span className="ml-1 text-[#9ec043]">· {documents.length} Dokumente</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {msg && <span className={`text-xs ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`} data-testid="doc-msg">{msg}</span>}
            <Button onClick={save} disabled={saving} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm h-8" data-testid="btn-save-doc">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? '…' : 'Speichern'}
            </Button>
            <Button onClick={downloadPdf} variant="outline" className="text-sm h-8" data-testid="btn-download-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            {data?.systemChecklist?.available && (
              <div className="inline-flex items-center">
                <Button
                  onClick={openSystemChecklist}
                  variant="outline"
                  className="text-sm h-8 border-[#9ec043] text-[#6d8a2e] hover:bg-[#f3f9e4] rounded-r-none"
                  data-testid="btn-open-systemchecklist"
                  title={
                    data.systemChecklist.forceCompleted
                      ? `Systemcheckliste (unvollständig abgeschlossen) ansehen · ${data.systemChecklist.fileName || ''}`
                      : `Systemcheckliste ansehen · erstellt ${data.systemChecklist.generatedAt?.slice(0, 10) || ''}`
                  }
                >
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
                  Systemcheckliste
                  {data.systemChecklist.forceCompleted && (
                    <AlertTriangle className="w-3 h-3 ml-1 text-amber-600" />
                  )}
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
                <Button
                  onClick={downloadSystemChecklist}
                  variant="outline"
                  className="text-sm h-8 px-2 border-[#9ec043] border-l-0 text-[#6d8a2e] hover:bg-[#f3f9e4] rounded-l-none"
                  data-testid="btn-download-systemchecklist"
                  title="Systemcheckliste herunterladen"
                >
                  <FileDown className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            <Button onClick={printDoc} variant="outline" className="text-sm h-8" data-testid="btn-print-doc">
              <Printer className="w-3.5 h-3.5 mr-1" /> Drucken
            </Button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="btn-close-doc"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* System tabs — only in multi-doc mode (Kanban-opened) */}
        {!docId && (
        <div className="px-5 pt-3 flex flex-wrap gap-1.5 items-center shrink-0" data-testid="doc-system-tabs">
          {documents.map(d => (
            <button key={d.id} onClick={() => setActiveDocId(d.id)}
              className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${activeDocId === d.id ? 'bg-[#9ec043] border-[#9ec043] text-white font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid={`doc-tab-${d.id}`}>
              <Layers className="w-3 h-3" />
              {d.systemName || 'Unbenannt'}
              {d.quantity > 1 && <span className="text-[10px] opacity-75">×{d.quantity}</span>}
            </button>
          ))}
          <AddSystemDropdown systems={systems} onAdd={addSystemDoc} />
          {!docId && !locked && (
            <button onClick={regenerateDocs}
              className="text-[11px] text-gray-500 hover:text-[#9ec043] inline-flex items-center gap-1 ml-1"
              title="Dokumente komplett neu aus den Auftragspositionen + Systemdatenbank erkennen"
              data-testid="btn-regenerate-docs">
              <Sparkles className="w-3 h-3" /> Neu erkennen
            </button>
          )}
          {documents.length > 1 && (
            <button onClick={deleteActiveDoc} className="text-[11px] text-red-500 hover:text-red-700 inline-flex items-center gap-1 ml-auto" data-testid="btn-del-active-doc">
              <Trash2 className="w-3 h-3" /> Dokument entfernen
            </button>
          )}
        </div>
        )}

        <div className="overflow-y-auto p-5 flex-1">
          {locked && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="doc-locked-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <strong>Lager wurde bereits gebucht</strong>
                {order.bomDeductedAt && <span className="text-amber-700"> · {new Date(order.bomDeductedAt).toLocaleString('de-DE')}</span>}
                <div className="mt-0.5 text-amber-800">
                  Nach dem Abschluss können nur noch <strong>Seriennummern</strong> angepasst werden
                  — z. B. bei Garantietausch. Komponenten und Mengen sind gesperrt. Jede Änderung
                  wird automatisch im Änderungs-Log protokolliert.
                </div>
                <button onClick={() => setShowAudit(true)} className="mt-1.5 text-[11px] text-amber-700 hover:text-amber-900 underline" data-testid="btn-show-audit-log">
                  Änderungs-Log anzeigen
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5" data-testid="doc-header">
            {(template.header || []).map(h => (
              <HeaderField key={h.key} field={h}
                value={values.header?.[h.key] || ''}
                onChange={(v) => setHeader(h.key, v)}
                runOCR={runOCR} ocrFor={ocrFor} setOcrFor={setOcrFor}
                locked={locked} />
            ))}
          </div>

          {/* Kommission explicit editable */}
          <div className="mb-5">
            <label className="text-[10px] uppercase tracking-wider text-gray-400">Kommission (für dieses Dokument)</label>
            <Input value={activeDoc.kommission || ''} onChange={e => setKommission(e.target.value)}
              disabled={locked}
              className="mt-0.5 text-sm h-9" placeholder="z. B. Kunde Meier – Garage" data-testid="doc-kommission" />
          </div>

          {/* Sections */}
          <div className="space-y-5">
            {(template.sections || []).map(sec => (
              <SectionBlock key={sec.id} section={sec}
                value={values.sections?.[sec.id]}
                onChange={(v) => setSection(sec.id, v)}
                runOCR={runOCR} ocrFor={ocrFor} setOcrFor={setOcrFor}
                inventory={inventory} locked={locked} />
            ))}
          </div>
        </div>
      </div>
      {showAudit && <AuditLogDialog docId={activeDoc.id} onClose={() => setShowAudit(false)} />}
      {cropReq && (
        <ImageCropDialog
          file={cropReq.file}
          hintLabel={cropReq.hint}
          onCancel={() => setCropReq(null)}
          onCropped={async (cropped) => {
            const { hint, cb } = cropReq;
            setCropReq(null);
            await performOcr(cropped, hint, cb);
          }}
        />
      )}
    </div>
  );
}

function AddSystemDropdown({ systems, onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="text-xs px-3 py-1.5 rounded-md border border-dashed border-gray-300 text-gray-500 hover:text-[#9ec043] hover:border-[#9ec043] inline-flex items-center gap-1"
        data-testid="btn-add-system-doc">
        <Plus className="w-3 h-3" /> System hinzufügen <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 max-h-64 overflow-auto min-w-[200px]" data-testid="add-system-menu">
          <button onClick={() => { setOpen(false); onAdd(null); }}
            className="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 text-gray-500 border-b border-gray-100">
            Leeres Dokument
          </button>
          {systems.map(s => (
            <button key={s.id} onClick={() => { setOpen(false); onAdd(s.id); }}
              className="w-full text-left text-xs px-3 py-1.5 hover:bg-[#9ec043]/10">
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderField({ field, value, onChange, runOCR, ocrFor, setOcrFor, locked }) {
  const busy = ocrFor?.headerKey === field.key;
  // 04.02.2026: Regex-Whitelist (serial|vrm|pin|dongle|password) war
  // fragil — Labels/Keys aus `db.document_template` variieren
  // (Groß-/Kleinschreibung, Umlaute, Custom-Feldnamen), wodurch die
  // Serial-Felder wieder gesperrt wurden. Wir spiegeln jetzt exakt
  // die Backend-Blacklist aus `order_documents.py` (LOCKED_HEADER_KEYS)
  // — nur diese 3 Keys bleiben nach `bomDeducted=True` gesperrt, alles
  // andere (Seriennummern, VRM-ID, Dongle-Passwort, Custom-Header) ist
  // editierbar.
  const LOCKED_HEADER_KEYS = ['fertigstellung', 'auftragsnummer', 'kommission'];
  const isBlacklisted = LOCKED_HEADER_KEYS.includes(String(field.key || '').toLowerCase());
  const disabled = locked && isBlacklisted;
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-400">{field.label}</label>
      <div className="flex items-center gap-1 mt-0.5">
        <Input type={field.type === 'date' ? 'date' : 'text'} value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="text-sm h-9 flex-1" data-testid={`doc-header-${field.key}`} />
        {field.ocr && (
          <label className={`p-2 rounded cursor-pointer border ${busy ? 'bg-[#9ec043]/10 border-[#9ec043] text-[#9ec043]' : 'border-gray-200 text-gray-400 hover:text-[#9ec043] hover:border-[#9ec043]'}`} title={`Per Foto erkennen: ${field.ocrHint || field.label}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={async e => {
                const f = e.target.files?.[0]; if (!f) return;
                setOcrFor({ headerKey: field.key });
                await runOCR(f, field.ocrHint || field.label, (res) => {
                  if (res.serial) onChange(res.serial);
                  else alert('Nicht erkannt. ' + (res.note || ''));
                });
                setOcrFor(null); e.target.value = '';
              }}
              data-testid={`doc-header-ocr-${field.key}`} />
          </label>
        )}
      </div>
    </div>
  );
}

function SectionBlock({ section, value, onChange, runOCR, ocrFor, setOcrFor, inventory, locked }) {
  const { id, label, type, options, allowCustom, editableModel, withNotes } = section;

  if (type === 'textarea') {
    return (
      <div data-testid={`doc-section-${id}`}>
        <h3 className="text-sm font-semibold text-[#32373c] mb-1.5">{label}</h3>
        <textarea value={value || ''} onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded p-2 text-sm min-h-20" rows={3}
          data-testid={`doc-textarea-${id}`} />
      </div>
    );
  }

  if (type === 'auto_product') {
    const v = value || {};
    return (
      <div className="bg-[#9ec043]/5 border border-[#9ec043]/30 rounded-lg p-3" data-testid={`doc-section-${id}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Package className="w-4 h-4 text-[#9ec043]" /> {label}
          </h3>
          {v.quantity > 1 && <span className="text-[11px] text-[#9ec043] font-medium">× {v.quantity}</span>}
        </div>
        <div className="mt-2 text-base font-semibold text-[#32373c]">
          {v.systemName || <span className="text-gray-400 italic text-sm">Kein System erkannt — bitte Auftrag prüfen</span>}
        </div>
      </div>
    );
  }

  if (type === 'auto_component') {
    const rows = value || [];
    const busyIdx = ocrFor?.section === id ? ocrFor.index : null;
    // 27.10.2026 (#5): Batterietyp-Dropdown pro Zeile im Batterie-Abschnitt.
    // Der gewählte Typ steuert Reservierung & Lagerabzug (batteryType).
    const isBatterySection = id === 'batterien' || (/batterie/i.test(label || '') && !/wechselrichter/i.test(label || '') && !/kabel/i.test(label || ''));
    const isCableSection = id === 'batteriekabel' || /batteriekabel|batterie[\s-]?kabel/i.test(label || '');
    const BATTERY_TYPES = ['Pytes V5', 'Pytes V5a', 'Pytes 48100', 'Pytes 48100 TE', 'Voltsmile E-Serie 5,12 weiß', 'Voltsmile EE-Serie 5,12 grau'];
    const BATTERY_CABLES = [
      'Pytes Batteriekabel 4800 - rot', 'Pytes Batteriekabel 4800 - schwarz',
      'Pytes Batteriekabel 4800 TE - rot', 'Pytes Batteriekabel 4800 TE - schwarz',
      'Pytes Batteriekabel V5 - rot', 'Pytes Batteriekabel V5 - schwarz',
      'Pytes Batteriekabel V5a - rot', 'Pytes Batteriekabel V5a - schwarz',
    ];
    return (
      <div data-testid={`doc-section-${id}`}>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-[#32373c]">{label} ({rows.length})</h3>
          {!locked && (
          <button onClick={() => onChange([...rows, { modell: '', hersteller: '', serial: '' }])}
            className="text-[11px] text-[#9ec043] hover:underline inline-flex items-center gap-1" data-testid={`doc-${id}-add`}>
            <Plus className="w-3 h-3" /> Zeile hinzufügen
          </button>
          )}
        </div>
        {rows.length === 0 && <div className="text-xs text-gray-400 italic py-2">Keine passenden Komponenten im System gefunden.</div>}
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
              <tr><th className="text-left px-2 py-1.5 w-8">#</th>
                  <th className="text-left px-2 py-1.5">{isCableSection ? 'Batteriekabel' : isBatterySection ? 'Batterietyp' : 'Modell'}</th>
                  {!isCableSection && <th className="text-left px-2 py-1.5 w-72">Seriennummer</th>}
                  <th className="w-10" /></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, i) => {
                const busy = busyIdx === i;
                return (
                  <tr key={i}>
                    <td className="px-2 py-1.5 text-xs text-gray-400 text-center tabular-nums">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      {isCableSection ? (
                        <select
                          value={r.cableType || ''}
                          disabled={locked}
                          onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, cableType: e.target.value } : x))}
                          className="text-sm h-7 border border-gray-200 rounded px-1 w-full bg-white disabled:bg-gray-50"
                          data-testid={`doc-${id}-cabletype-${i}`}>
                          <option value="">— Batteriekabel wählen —</option>
                          {BATTERY_CABLES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : isBatterySection ? (
                        <select
                          value={r.batteryType || ''}
                          disabled={locked}
                          onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, batteryType: e.target.value } : x))}
                          className="text-sm h-7 border border-gray-200 rounded px-1 w-full bg-white disabled:bg-gray-50"
                          data-testid={`doc-${id}-batterytype-${i}`}>
                          <option value="">— Batterie wählen —</option>
                          {BATTERY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      ) : editableModel ? (
                        <Input value={r.modell || ''} onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, modell: e.target.value } : x))}
                          disabled={locked}
                          className="text-sm h-7 border-gray-200" data-testid={`doc-${id}-modell-${i}`} />
                      ) : (
                        <span className="text-[#32373c]">{r.modell}</span>
                      )}
                      {!isBatterySection && !isCableSection && r.hersteller && <div className="text-[10px] text-gray-400 mt-0.5">{r.hersteller}</div>}
                    </td>
                    {!isCableSection && (
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <Input value={r.serial || ''} onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, serial: e.target.value } : x))}
                          placeholder="Seriennummer" className="text-sm h-7 border-gray-200 font-mono"
                          data-testid={`doc-${id}-serial-${i}`} />
                        <OcrIcon busy={busy} onFile={async (f) => {
                          setOcrFor({ section: id, index: i });
                          await runOCR(f, `Seriennummer: ${r.modell}`, (res) => {
                            if (res.serial) onChange(rows.map((x, j) => i === j ? { ...x, serial: res.serial, _ocrConfidence: res.confidence } : x));
                            else alert('Keine Seriennummer erkannt. ' + (res.note || ''));
                          });
                          setOcrFor(null);
                        }} testid={`doc-${id}-ocr-${i}`} />
                      </div>
                      {r._ocrConfidence != null && <div className="text-[9px] text-gray-400 mt-0.5 inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> KI ({Math.round(r._ocrConfidence * 100)}%)</div>}
                    </td>
                    )}
                    <td className="px-2 py-1.5 text-right">
                      {!locked && (
                      <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (type === 'select' || type === 'select_serial') {
    // Defensive: ein Document aus früherer Migration könnte hier einen
    // String oder Object haben (statt einer Array-Liste von Optionen).
    // In dem Fall fallen wir auf die Template-Defaults zurück, damit
    // .map() nicht crasht und das gesamte Begleitdokument als weißes
    // Fenster endet.
    const safeOptions = Array.isArray(options) ? options : [];
    const rows = Array.isArray(value) && value.length > 0
      ? value
      : safeOptions.map(opt => ({ option: opt, checked: false, serial: '' }));
    return (
      <div data-testid={`doc-section-${id}`}>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-[#32373c]">{label}</h3>
          {allowCustom && type === 'select_serial' && (
            <button onClick={() => onChange([...rows, { option: '', checked: true, serial: '', custom: true }])}
              className="text-[11px] text-[#9ec043] hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Sonderversion</button>
          )}
        </div>
        {type === 'select' ? (
          <div className="flex flex-wrap gap-2">
            {rows.map((r, i) => (
              <label key={i} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border cursor-pointer ${r.checked ? 'bg-[#9ec043]/10 border-[#9ec043] text-[#9ec043] font-medium' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <input type="checkbox" checked={!!r.checked} className="accent-[#9ec043]"
                  onChange={() => onChange(rows.map((x, j) => i === j ? { ...x, checked: !x.checked } : x))}
                  data-testid={`doc-${id}-check-${i}`} />{r.option}
              </label>
            ))}
          </div>
        ) : (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
                <tr><th className="w-8 px-2 py-1.5">✓</th>
                    <th className="text-left px-2 py-1.5">Option</th>
                    <th className="text-left px-2 py-1.5 w-72">Seriennummer</th>
                    <th className="w-10" /></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => {
                  const busy = ocrFor?.section === id && ocrFor?.index === i;
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!r.checked} className="accent-[#9ec043]"
                        onChange={() => onChange(rows.map((x, j) => i === j ? { ...x, checked: !x.checked } : x))}
                        data-testid={`doc-${id}-check-${i}`} /></td>
                      <td className="px-2 py-1.5">
                        {r.custom ? (
                          <Input value={r.option} onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, option: e.target.value } : x))}
                            placeholder="Eigene Bezeichnung" className="text-sm h-7 border-gray-200" />
                        ) : <span className="text-[#32373c]">{r.option}</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <Input value={r.serial || ''} onChange={e => onChange(rows.map((x, j) => i === j ? { ...x, serial: e.target.value } : x))}
                            placeholder="Seriennummer" className="text-sm h-7 border-gray-200 font-mono"
                            data-testid={`doc-${id}-serial-${i}`} />
                          <OcrIcon busy={busy} onFile={async (f) => {
                            setOcrFor({ section: id, index: i });
                            await runOCR(f, r.option, (res) => {
                              if (res.serial) onChange(rows.map((x, j) => i === j ? { ...x, serial: res.serial } : x));
                              else alert('Nicht erkannt. ' + (res.note || ''));
                            });
                            setOcrFor(null);
                          }} />
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {r.custom && <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-gray-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  if (type === 'inventory_pick') {
    const v = value || { items: [], notes: '' };
    const items = v.items || [];
    // Legacy → neues Serials-Array. Backend bleibt kompatibel: wir schreiben
    // sowohl `serials` (neu) als auch `serial` (Legacy = Komma-Join) zurück.
    const norm = (it) => {
      const cnt = Math.max(1, parseInt(it.anzahl, 10) || 1);
      const existing = Array.isArray(it.serials) ? it.serials.slice(0, cnt)
        : (it.serial ? [it.serial] : []);
      const serials = Array.from({ length: cnt }, (_, k) => existing[k] || '');
      return { ...it, anzahl: cnt, serials };
    };
    const addItem = () => onChange({ ...v, items: [...items, { itemId: '', modell: '', hersteller: '', anzahl: 1, serial: '', serials: [''] }] });
    const updItem = (i, patch) => onChange({
      ...v,
      items: items.map((x, j) => {
        if (j !== i) return x;
        const merged = { ...x, ...patch };
        // Anzahl geändert → serials-Array anpassen (kürzer/länger)
        if (patch.anzahl !== undefined) {
          const cnt = Math.max(1, parseInt(patch.anzahl, 10) || 1);
          const cur = Array.isArray(merged.serials) ? merged.serials
            : (merged.serial ? [merged.serial] : []);
          const next = Array.from({ length: cnt }, (_, k) => cur[k] || '');
          merged.serials = next;
          merged.serial = next.filter(Boolean).join(', ');
        }
        if (patch.serials !== undefined) {
          merged.serial = patch.serials.filter(Boolean).join(', ');
        }
        return merged;
      }),
    });
    const delItem = (i) => onChange({ ...v, items: items.filter((_, j) => j !== i) });
    const busyIdx = ocrFor?.section === id ? ocrFor.index : null;

    return (
      <div data-testid={`doc-section-${id}`}>
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-sm font-semibold text-[#32373c]">{label}</h3>
          <button onClick={addItem} className="text-[11px] text-[#9ec043] hover:underline inline-flex items-center gap-1" data-testid={`doc-${id}-add`}>
            <Plus className="w-3 h-3" /> Artikel aus Lager
          </button>
        </div>
        {items.length > 0 && (
          <div className="border border-gray-100 rounded-lg overflow-hidden mb-2">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
                <tr><th className="text-left px-2 py-1.5">Artikel</th>
                    <th className="text-right px-2 py-1.5 w-20">Anzahl</th>
                    <th className="text-left px-2 py-1.5 w-80">Seriennummer(n)</th>
                    <th className="w-10" /></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((rawIt, i) => {
                  const it = norm(rawIt);
                  const busy = busyIdx === i;
                  return (
                    <tr key={i}>
                      <td className="px-2 py-1.5 align-top">
                        <InventoryAutocomplete
                          value={it.itemId}
                          modell={it.modell}
                          hersteller={it.hersteller}
                          inventory={inventory}
                          onPick={(inv) => updItem(i, {
                            itemId: inv.id,
                            modell: inv.modell || '',
                            hersteller: inv.hersteller || '',
                          })}
                          testid={`doc-${id}-picker-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right align-top">
                        <Input type="number" min="1" value={it.anzahl}
                          onChange={e => updItem(i, { anzahl: e.target.value })}
                          className="text-sm h-7 text-right tabular-nums border-gray-200 w-16 ml-auto"
                          data-testid={`doc-${id}-qty-${i}`} />
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        <div className="space-y-1">
                          {it.serials.map((s, sIdx) => (
                            <div key={sIdx} className="flex items-center gap-1">
                              {it.anzahl > 1 && (
                                <span className="text-[10px] text-gray-400 w-4 shrink-0">#{sIdx + 1}</span>
                              )}
                              <Input value={s}
                                     onChange={e => {
                                       const next = [...it.serials];
                                       next[sIdx] = e.target.value;
                                       updItem(i, { serials: next });
                                     }}
                                     placeholder={`Seriennummer ${it.anzahl > 1 ? sIdx + 1 : ''}`.trim() + ' (optional)'}
                                     className="text-sm h-7 border-gray-200 font-mono flex-1"
                                     data-testid={`doc-${id}-serial-${i}-${sIdx}`} />
                              <OcrIcon busy={busy && sIdx === 0} onFile={async (f) => {
                                setOcrFor({ section: id, index: i });
                                await runOCR(f, `Seriennummer: ${it.modell}`, (res) => {
                                  if (res.serial) {
                                    const next = [...it.serials];
                                    next[sIdx] = res.serial;
                                    updItem(i, { serials: next });
                                  } else alert('Nicht erkannt. ' + (res.note || ''));
                                });
                                setOcrFor(null);
                              }} />
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right align-top">
                        <button onClick={() => delItem(i)} className="text-gray-300 hover:text-red-500 p-1"><Trash2 className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {withNotes && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400">Instruktionen / Notizen</label>
            <textarea value={v.notes || ''} onChange={e => onChange({ ...v, notes: e.target.value })}
              className="w-full mt-0.5 border border-gray-200 rounded p-2 text-sm min-h-16" rows={2}
              data-testid={`doc-${id}-notes`} />
          </div>
        )}
      </div>
    );
  }
  return null;
}

function OcrIcon({ busy, onFile, testid }) {
  return (
    <label className={`p-1.5 rounded cursor-pointer border ${busy ? 'bg-[#9ec043]/10 border-[#9ec043] text-[#9ec043]' : 'border-gray-200 text-gray-400 hover:text-[#9ec043] hover:border-[#9ec043]'}`} title="Seriennummer per Foto erkennen">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
      <input type="file" accept="image/*" capture="environment" className="hidden"
        onChange={async e => { const f = e.target.files?.[0]; if (!f) return; await onFile(f); e.target.value = ''; }}
        data-testid={testid} />
    </label>
  );
}

/**
 * Autocomplete für Lagerartikel — ersetzt das lange `<select>` mit
 * hunderten Optionen im Zubehör-/Sonderbau-Editor (User-Spec 25.08.2026).
 * Sucht in Hersteller + Modell (kleines gemeinsames Vielfaches).
 */
function InventoryAutocomplete({ value, modell, hersteller, inventory, onPick, testid }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const chosen = value ? inventory.find(x => x.id === value) : null;
  const label = chosen
    ? `${chosen.hersteller ? chosen.hersteller + ' · ' : ''}${chosen.modell}`
    : (modell ? `${hersteller ? hersteller + ' · ' : ''}${modell}` : '');
  const rx = q.trim().toLowerCase();
  const hits = rx
    ? inventory.filter(x => `${x.hersteller || ''} ${x.modell || ''}`.toLowerCase().includes(rx)).slice(0, 10)
    : inventory.slice(0, 10);
  if (chosen && !open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
              className="text-sm h-7 border border-gray-200 rounded px-2 w-full text-left hover:border-[#9ec043]"
              data-testid={testid}>
        <span className="truncate inline-block max-w-full">{label}</span>
        <span className="text-[10px] text-gray-400 ml-1">· Lager: {chosen.stock}</span>
      </button>
    );
  }
  return (
    <div className="relative">
      <input
        autoFocus={open}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={label || 'Artikel suchen … (z. B. ET 340)'}
        className="text-sm h-7 border border-gray-200 rounded px-2 w-full"
        data-testid={testid}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onFocus={() => setOpen(true)}
      />
      {(open || q) && hits.length > 0 && (
        <div className="absolute z-20 bg-white border border-gray-200 rounded shadow mt-0.5 w-96 max-h-72 overflow-y-auto">
          {hits.map(h => (
            <button key={h.id} type="button" onMouseDown={() => { onPick(h); setQ(''); setOpen(false); }}
                    className="block w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 border-b border-gray-50 last:border-b-0">
              <div>{h.hersteller ? `${h.hersteller} · ` : ''}{h.modell}</div>
              <div className="text-[10px] text-gray-400">Lager: {h.stock}{h.article_number ? ` · Art.-Nr. ${h.article_number}` : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function renderPrintableHtml(template, doc, order) {
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const values = doc.values || {};
  const headerCells = (template.header || []).map(h => {
    const v = values.header?.[h.key] || '';
    return `<div class="hcell"><div class="hlabel">${esc(h.label)}</div><div class="hvalue">${esc(v)}</div></div>`;
  }).join('');

  const sectionsHtml = (template.sections || []).map(sec => {
    const vv = values.sections?.[sec.id];
    if (sec.type === 'textarea') {
      if (!vv) return '';
      return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="note">${esc(vv)}</div></div>`;
    }
    if (sec.type === 'auto_product') {
      const v = vv || {};
      if (!v.systemName) return '';
      return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="product">${esc(v.systemName)}${v.quantity > 1 ? ` <span class="qty">× ${v.quantity}</span>` : ''}</div></div>`;
    }
    if (sec.type === 'auto_component') {
      const rows = vv || [];
      if (!rows.length) return '';
      return `<div class="sec"><h3>${esc(sec.label)} (${rows.length})</h3>
        <table><thead><tr><th style="width:28px">#</th><th>Modell</th><th>Seriennummer</th></tr></thead>
        <tbody>${rows.map((r, i) => `<tr><td class="num">${i + 1}</td><td>${esc(r.modell)}${r.hersteller ? ` <span class="sub">${esc(r.hersteller)}</span>` : ''}</td><td class="ser">${esc(r.serial || '')}</td></tr>`).join('')}</tbody></table></div>`;
    }
    if (sec.type === 'select') {
      const rows = vv || [];
      const on = rows.filter(r => r.checked);
      if (!on.length) return '';
      return `<div class="sec"><h3>${esc(sec.label)}</h3><div class="chips">${on.map(r => `<span class="chip on">☑ ${esc(r.option)}</span>`).join('')}</div></div>`;
    }
    if (sec.type === 'select_serial') {
      const rows = (vv || []).filter(r => r.checked || r.serial);
      if (!rows.length) return '';
      return `<div class="sec"><h3>${esc(sec.label)}</h3>
        <table><thead><tr><th style="width:28px"></th><th>Option</th><th>Seriennummer</th></tr></thead>
        <tbody>${rows.map(r => `<tr><td class="ck">${r.checked ? '☑' : '☐'}</td><td>${esc(r.option)}</td><td class="ser">${esc(r.serial || '')}</td></tr>`).join('')}</tbody></table></div>`;
    }
    if (sec.type === 'inventory_pick') {
      const v = vv || {};
      const items = v.items || [];
      if (!items.length && !v.notes) return '';
      const tableHtml = items.length ? `
        <table><thead><tr><th>Artikel</th><th style="width:60px">Anzahl</th><th>Seriennummer</th></tr></thead>
        <tbody>${items.map(it => `<tr><td>${esc(it.hersteller ? it.hersteller + ' · ' : '')}${esc(it.modell)}</td><td class="num">${esc(it.anzahl ?? '')}</td><td class="ser">${esc(it.serial || '')}</td></tr>`).join('')}</tbody></table>` : '';
      const notesHtml = v.notes ? `<div class="note">${esc(v.notes)}</div>` : '';
      return `<div class="sec"><h3>${esc(sec.label)}</h3>${tableHtml}${notesHtml}</div>`;
    }
    return '';
  }).join('');

  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${esc(template.title)} – ${esc(order.easybillDocumentNumber || '')} – ${esc(doc.systemName || '')}</title>
  <style>
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1d2229; font-size: 11pt; }
    h1 { font-size: 16pt; margin: 0 0 2mm; color: #32373c; }
    h3 { font-size: 11pt; margin: 3mm 0 1.5mm; color: #32373c; border-bottom: 0.3mm solid #9ec043; padding-bottom: 1mm; }
    .meta { color: #5b6470; font-size: 9pt; margin-bottom: 5mm; }
    .product { font-size: 16pt; font-weight: 700; color: #9ec043; padding: 3mm 0; }
    .product .qty { font-size: 12pt; color: #5b6470; font-weight: 400; }
    .hgrid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3mm; margin-bottom: 5mm; }
    .hcell { border: 0.3mm solid #ccc; padding: 1.5mm 2mm; border-radius: 1mm; }
    .hlabel { font-size: 7pt; letter-spacing: 0.5px; color: #7a8490; text-transform: uppercase; }
    .hvalue { font-size: 10pt; color: #1d2229; min-height: 5mm; }
    .sec { margin-bottom: 4mm; page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
    th { text-align: left; padding: 1mm 1.5mm; background: #f5f6f7; border-bottom: 0.2mm solid #ccc; font-weight: 600; font-size: 8.5pt; color: #5b6470; text-transform: uppercase; letter-spacing: 0.5px; }
    td { padding: 1mm 1.5mm; border-bottom: 0.2mm solid #eee; }
    .ck { width: 6mm; text-align: center; font-size: 12pt; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .ser { font-family: 'Courier New', monospace; color: #32373c; }
    .sub { font-size: 8pt; color: #7a8490; }
    .chips { display: flex; flex-wrap: wrap; gap: 2mm; }
    .chip { border: 0.2mm solid #9ec043; color: #32373c; padding: 0.5mm 2mm; border-radius: 3mm; }
    .note { border: 0.2mm solid #ccc; padding: 2mm; min-height: 8mm; border-radius: 1mm; white-space: pre-wrap; font-size: 9.5pt; }
    .sig { margin-top: 8mm; display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; page-break-inside: avoid; }
    .sig-box { border-top: 0.2mm solid #32373c; padding-top: 1mm; font-size: 8.5pt; color: #5b6470; min-height: 14mm; }
    .fob { text-align: right; font-size: 7.5pt; color: #9aa3ac; margin-top: 6mm; }
  </style></head><body>
    <h1>${esc(template.title || 'Produktbegleitdokument')}</h1>
    ${doc.systemName ? `<div style="font-size:22pt;font-weight:800;color:#9ec043;margin:-2mm 0 4mm;letter-spacing:0.5px;">${esc(doc.systemName)}${doc.quantity > 1 ? ` <span style="font-size:14pt;color:#5b6470;font-weight:500;">× ${doc.quantity}</span>` : ''}</div>` : ''}
    <div class="meta">Auftrag <strong>${esc(order.easybillDocumentNumber || '')}</strong>
      · ${esc(order.customerCompany || order.customerName || '')}
      ${doc.kommission ? ` · <strong>${esc(doc.kommission)}</strong>` : ''}
      ${order.easybillDocumentDate ? ` · ${esc(order.easybillDocumentDate)}` : ''}
    </div>
    <div class="hgrid">${headerCells}</div>
    ${sectionsHtml}
    <div class="sig">
      <div class="sig-box">Unterschrift Monteur</div>
      <div class="sig-box">Unterschrift Kunde / Datum</div>
    </div>
    <div class="fob">EES Effiziente Energiespeichersysteme GmbH · erstellt am ${new Date().toLocaleString('de-DE')}</div>
  </body></html>`;
}


function AuditLogDialog({ docId, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await authFetch(`/api/order-documents/${docId}/audit`);
      if (r.ok) {
        const d = await r.json();
        setRows(d.entries || []);
      } else {
        setRows([]);
      }
      setLoading(false);
    })();
  }, [docId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
           onClick={e => e.stopPropagation()} data-testid="audit-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-[#32373c]">Änderungs-Log · Seriennummern nach Lager-Buchung</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-sm text-gray-400">Lade …</div>
          ) : !rows || rows.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-8">
              Noch keine Änderungen seit der Lager-Buchung. Sobald eine Seriennummer
              angepasst wird, erscheint hier ein Audit-Eintrag.
            </div>
          ) : (
            <div className="space-y-2" data-testid="audit-list">
              {rows.map(r => (
                <div key={r.id} className="border border-gray-200 rounded-lg p-3 text-xs">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-[10px] text-gray-500">{r.path}</span>
                    <span className="text-[10px] text-gray-400">{new Date(r.changedAt).toLocaleString('de-DE')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div className="bg-red-50 border border-red-100 rounded p-2">
                      <div className="text-[9px] uppercase tracking-wider text-red-500 mb-0.5">Alt</div>
                      <div className="font-mono text-[11px] text-red-700 break-all">{r.oldValue || '—'}</div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded p-2">
                      <div className="text-[9px] uppercase tracking-wider text-emerald-600 mb-0.5">Neu</div>
                      <div className="font-mono text-[11px] text-emerald-700 break-all">{r.newValue || '—'}</div>
                    </div>
                  </div>
                  <div className="mt-1 text-[10px] text-gray-500">
                    von <strong>{r.changedByName || r.changedBy}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Default-Export: wrappt den eigentlichen Editor in ErrorBoundary,
// damit auch unerwartete Render-Crashes (z. B. fehlerhaftes Template
// oder defektes Dokument auf Production) sichtbar werden statt zu
// einem leeren weißen Fenster zu führen.
export default function OrderDocumentEditor(props) {
  return (
    <EditorErrorBoundary onClose={props.onClose}>
      <OrderDocumentEditorInner {...props} />
    </EditorErrorBoundary>
  );
}
